import { createContext, useContext, type PropsWithChildren } from "react";
import * as c from "@yahshi/contracts";
import { createAdminRequest, type AdminHttpOptions } from "../../lib/adminHttp";

export interface StorageApi {
  list(): Promise<c.StorageState>;
  create(input: c.StorageCreateInput): Promise<c.StorageConfiguration>;
  update(id: string, input: c.StorageUpdateInput): Promise<c.StorageConfiguration>;
  test(id: string, version: number): Promise<{ ok: true; message: string }>;
  setDefault(id: string | null, stateVersion: number): Promise<c.StorageState>;
  usage(id: string): Promise<c.StorageUsage>;
  refreshUsage(id: string, version: number): Promise<c.StorageUsage>;
  finance(id: string): Promise<c.StorageFinance>;
  refreshFinance(id: string, version: number): Promise<c.StorageFinance>;
  setPolicy(id: string, input: c.StoragePolicyInput): Promise<c.StorageUsage>;
}
const Context = createContext<StorageApi | null>(null);
export const StorageProvider = ({ api, children }: PropsWithChildren<{ api: StorageApi }>) => <Context.Provider value={api}>{children}</Context.Provider>;
export function useStorageApi() { const api = useContext(Context); if (!api) throw new Error("StorageProvider required"); return api; }
export function createHttpStorageApi(options: AdminHttpOptions): StorageApi {
  const request = createAdminRequest(options), base = "/admin/settings/storage";
  const json = (method: string, body: unknown) => ({ method, body: JSON.stringify(body) });
  const path = (id: string) => `${base}/${encodeURIComponent(c.storageConfigurationSchema.shape.id.parse(id))}`;
  return {
    list: () => request(base, c.storageStateSchema),
    create: input => request(base, c.storageConfigurationSchema, json("POST", c.storageCreateSchema.parse(input))),
    update: (id, input) => request(path(id), c.storageConfigurationSchema, json("PATCH", c.storageUpdateSchema.parse(input))),
    test: (id, version) => request(`${path(id)}/test`, c.storageTestResultSchema, json("POST", { expectedVersion: version })),
    setDefault: (configId, expectedStateVersion) => request(`${base}/default`, c.storageStateSchema, json("POST", { configId, expectedStateVersion })),
    usage: id => request(`${path(id)}/usage`, c.storageUsageSchema),
    refreshUsage: (id, expectedVersion) => request(`${path(id)}/usage`, c.storageUsageSchema, json("POST", { expectedVersion })),
    finance: id => request(`${path(id)}/finance`, c.storageFinanceSchema),
    refreshFinance: (id, expectedVersion) => request(`${path(id)}/finance`, c.storageFinanceSchema, json("POST", { expectedVersion })),
    setPolicy: (id, input) => request(`${path(id)}/limits`, c.storageUsageSchema, json("PUT", c.storagePolicyInputSchema.parse(input))),
  };
}
export function createDevelopmentStorageApi(): StorageApi {
  let state: c.StorageState = { items: [], defaultConfigId: null, version: 1 };
  const clone = <T,>(value: T): T => structuredClone(value);
  const unavailable = async (): Promise<c.StorageUsage> => { throw new Error("用量和限额管理请在 ECS 环境使用"); };
  return {
    usage: unavailable, refreshUsage: unavailable, setPolicy: unavailable,
    async finance() { throw new Error("账户费用查询请在 ECS 环境使用"); },
    async refreshFinance() { throw new Error("账户费用查询请在 ECS 环境使用"); },
    async list() { return clone(state); },
    async create(input) {
      const value = c.storageCreateSchema.parse(input), now = new Date().toISOString();
      const config: c.StorageConfiguration = { id: crypto.randomUUID(), name: value.name, location: value.location, version: 1, credentialsConfigured: true, testedVersion: null, testedAt: null, createdAt: now, updatedAt: now };
      state.items.unshift(config); return clone(config);
    },
    async update(id, input) {
      const value = c.storageUpdateSchema.parse(input), row = state.items.find(item => item.id === id);
      if (!row || row.version !== value.expectedVersion) throw new Error("配置已更新，请刷新后重试");
      row.name = value.name; row.version++; row.updatedAt = new Date().toISOString(); row.testedVersion = null; return clone(row);
    },
    async test() { throw new Error("本地演示模式不执行云厂商连接测试，请在 ECS 环境使用"); },
    async setDefault(id, version) {
      if (version !== state.version) throw new Error("默认存储已更新，请刷新");
      if (id) throw new Error("本地演示模式不连接云存储");
      state = { ...state, defaultConfigId: null, version: state.version + 1 }; return clone(state);
    },
  };
}
